<template>
  <Teleport to="body">
    <!-- Drawer -->
    <div
      class="fixed-bottom full-width bg-white"
      :class="`drawer-${state}`"
      :style="drawerStyle"
    >
      <!-- Handle -->
      <div
        v-touch-pan.mouse.vertical.prevent="onPan"
        class="row justify-center q-py-md cursor-grab drawer-handle-container"
        :aria-label="`Drawer handle, currently ${state}. Click to cycle states or drag to resize.`"
        :aria-expanded="state !== 'closed'"
        role="button"
        @click="!isDragging && cycleState($event)"
      >
        <div class="drawer-handle bg-grey-5" />
      </div>

      <!-- Content -->
      <div
        class="drawer-content"
        :style="contentStyle"
      >
        <slot
          :state="state"
          :height="currentHeight"
          :progress="progress"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar'
import { computed, ref, watch } from 'vue'

type DrawerState = 'closed' | 'peek' | 'half' | 'full'

interface Props {
  modelValue?: boolean
  snapPoints?: DrawerState[]
  peekHeight?: number
  halfRatio?: number
  topOffset?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  snapPoints: () => ['closed', 'peek', 'half', 'full'],
  peekHeight: 140,
  halfRatio: 0.5,
  topOffset: 60,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'state-change': [state: DrawerState]
}>()

// Reactive state
const state = ref<DrawerState>('closed')
const currentHeight = ref(36) // Start with handle visible
const isDragging = ref(false)
const startHeight = ref(0)
const dragOffset = ref(0)
const q = useQuasar()

// Heights for each state
const heights = computed(() => {
  const screenHeight = q.screen.height
  const maxHeight = screenHeight - props.topOffset

  return {
    closed: 36, // Show handle only
    peek: props.peekHeight,
    half: Math.round(maxHeight * props.halfRatio),
    full: maxHeight,
  }
})

// Progress ratio (0-1)
const progress = computed(() => {
  if (heights.value.full === 0) return 0
  return currentHeight.value / heights.value.full
})

// Dynamic styles
const drawerStyle = computed(() => {
  const maxHeight = heights.value.full
  let visibleHeight = currentHeight.value

  // During drag, apply the offset for immediate visual feedback
  if (isDragging.value) {
    visibleHeight = Math.max(
      heights.value.closed,
      Math.min(heights.value.full, startHeight.value - dragOffset.value)
    )
  }

  const hiddenHeight = maxHeight - visibleHeight

  return {
    transform: `translateY(${hiddenHeight}px)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 6000,
    borderRadius: visibleHeight >= heights.value.full ? '0' : '16px 16px 0 0',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
    height: `${maxHeight}px`,
  }
})

const contentStyle = computed(() => {
  let effectiveHeight = currentHeight.value

  // During drag, use the same visible height logic as drawerStyle
  if (isDragging.value) {
    effectiveHeight = Math.max(
      heights.value.closed,
      Math.min(heights.value.full, startHeight.value - dragOffset.value)
    )
  }

  const availableHeight = effectiveHeight - 36 // 36px for handle
  return {
    height: `${Math.max(0, availableHeight)}px`,
    maxHeight: `${Math.max(0, availableHeight)}px`,
  }
})

// Dragging logic
function onPan(ev: Record<string, unknown>) {
  const isFirst = ev.isFirst as boolean
  const isFinal = ev.isFinal as boolean
  const offset = ev.offset as { x: number; y: number }

  if (isFirst) {
    isDragging.value = true
    startHeight.value = currentHeight.value
    dragOffset.value = 0
    return
  }

  if (isFinal) {
    // Calculate final height and update currentHeight
    const finalHeight = Math.max(
      heights.value.closed,
      Math.min(heights.value.full, startHeight.value - dragOffset.value)
    )

    isDragging.value = false
    currentHeight.value = finalHeight
    dragOffset.value = 0

    snapToNearest()
    return
  }

  // Update drag offset for visual feedback (using offset.y which is cumulative from start)
  dragOffset.value = offset.y
}

function snapToNearest() {
  const availableHeights = props.snapPoints.map((s) => heights.value[s])

  const closest = availableHeights.reduce((prev, curr) =>
    Math.abs(curr - currentHeight.value) < Math.abs(prev - currentHeight.value) ? curr : prev
  )
  const newState = Object.entries(heights.value).find(([, h]) => h === closest)?.[0] as DrawerState

  if (newState) {
    animateTo(newState)
  }
}

function updateState() {
  const h = currentHeight.value
  let newState: DrawerState = 'closed'

  if (h <= 50)
    newState = 'closed' // Handle + small buffer
  else if (h <= props.peekHeight + 20) newState = 'peek'
  else if (h <= heights.value.half + 40) newState = 'half'
  else newState = 'full'

  if (newState !== state.value) {
    state.value = newState
    emit('state-change', newState)
    emit('update:modelValue', newState !== 'closed')
  }
}

function animateTo(targetState: DrawerState) {
  const targetHeight = heights.value[targetState]

  // Let CSS transition handle the animation for smoother results
  currentHeight.value = targetHeight
  updateState()
}

// Public methods
function open(targetState: DrawerState = 'half') {
  if (props.snapPoints.includes(targetState)) {
    animateTo(targetState)
  }
}

function close() {
  animateTo('closed')
}

function cycleState(event: Event) {
  // Prevent cycling during drag operations
  if (isDragging.value) {
    event.preventDefault()
    return
  }

  // Find current position in snapPoints and cycle to next
  const currentIndex = props.snapPoints.indexOf(state.value)
  const nextIndex = (currentIndex + 1) % props.snapPoints.length
  animateTo(props.snapPoints[nextIndex])
}

// Watch v-model
watch(
  () => props.modelValue,
  (val) => {
    if (val === undefined) return
    if (val && state.value === 'closed') {
      open()
    } else if (!val && state.value !== 'closed') {
      close()
    }
  },
  { immediate: true }
)

defineExpose({ open, close, state, currentHeight })
</script>

<style scoped lang="scss">
.drawer-handle-container {
  box-shadow: $re-shadow-light;
}

.drawer-handle {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  transition: all 0.2s;

  .cursor-grab:active & {
    width: 50px;
    background-color: var(--q-grey-6);
  }
}

.drawer-content {
  overscroll-behavior: contain;
}

.drawer-closed,
.drawer-peek,
.drawer-half,
.drawer-full {
  pointer-events: auto;
}

.drawer-closed .drawer-content {
  pointer-events: none;
}
</style>
